'use client'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import apiGet from '@/store/helpers/api-get'
import { ExternalLinkIcon } from 'lucide-react'
import { useParams } from 'next/navigation'
import useSWR from 'swr'

export default function StandingsTable() {
  const params = useParams()
  const { data: standings } = useSWR(
    {
      url: '/analytics/standings',
      query: { seasonId: params.seasonId, isVirtual: false },
    },
    apiGet,
  )

  function getOrdinalFromPositionString(positionString) {
    return positionString.split(' out of')[0]
  }
  function getNumberWithOrdinal(n) {
    var s = ['th', 'st', 'nd', 'rd'],
      v = n % 100
    return n + (s[(v - 20) % 10] || s[v] || s[0])
  }

  return (
    <div className="w-full">
      <div className="p-6 desktop-xs:p-12 desktop-xs:pb-8">
        <h5 className="pb-3 text-h-5 font-semibold desktop-xs:mb-6">
          Standings
        </h5>
        <p className="w-full text-left text-p-2 text-neutral-blue-500 desktop-xs:mb-4">
          Overall finishing positions for Season {params.seasonId.at(-1)}
        </p>
      </div>
      <div className="divide bg-neutral-blue-700"></div>

      <Table
        border="1"
        className="dataframe w-full overflow-x-auto whitespace-nowrap max-desktop-xs:block"
      >
        <TableHeader>
          <TableRow className="semibold-caps text-right text-cap">
            <TableHead className="sticky -left-1 top-0 z-10 bg-neutral-blue-800 text-center">
              POS
            </TableHead>
            <TableHead className="pl-2">Driver</TableHead>
            <TableHead className="pl-2">Country</TableHead>
            <TableHead className="text-center">Rds Entered</TableHead>
            <TableHead className="text-center">Pos pts</TableHead>
            <TableHead className="text-center">pace pts</TableHead>
            <TableHead className="text-center">safety pts</TableHead>
            <TableHead className="text-center">total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody className="datatables-light">
          {standings
            ?.filter((standing) => standing.is_virtual == false)
            .map((standing, index) => {
              return (
                <TableRow
                  key={`${standing.driver_name}-${index}`}
                  className="group cursor-pointer tabular-nums [&>td]:transition-all"
                  onClick={() =>
                    window.open(
                      `/analytics/${params.seasonId}/${standing.driver_id}`,
                    )
                  }
                >
                  <TableCell className="sticky -left-1 z-10 border-neutral-blue-700 bg-neutral-blue-800 text-center group-hover:bg-neutral-blue-700">
                    {getNumberWithOrdinal(index + 1)}
                  </TableCell>
                  <TableCell className="flex items-center gap-2 group-hover:bg-neutral-blue-700">
                    {standing.driver_name}{' '}
                    <ExternalLinkIcon className="ml-1 h-4 w-4 stroke-shades-0 stroke-2" />
                  </TableCell>
                  <TableCell className="text-neutral-blue-100 group-hover:bg-neutral-blue-700">
                    {standing.country}
                  </TableCell>
                  <TableCell className="text-center text-neutral-blue-100 group-hover:bg-neutral-blue-700">
                    {standing.rounds_completed}
                  </TableCell>
                  <TableCell className="text-center text-neutral-blue-100 group-hover:bg-neutral-blue-700">
                    {standing.position_points.toPrecision(4)} (
                    {getOrdinalFromPositionString(standing.position_standings)})
                  </TableCell>
                  <TableCell className="text-center text-neutral-blue-100 group-hover:bg-neutral-blue-700">
                    {standing.pace_points.toPrecision(4)} (
                    {getOrdinalFromPositionString(standing.pace_standings)})
                  </TableCell>
                  <TableCell className="text-center text-neutral-blue-100 group-hover:bg-neutral-blue-700">
                    {standing.safety_points.toPrecision(4)} (
                    {getOrdinalFromPositionString(standing.safety_standings)})
                  </TableCell>
                  <TableCell className="text-center group-hover:bg-neutral-blue-700">
                    {(
                      standing.position_points +
                      standing.pace_points +
                      standing.safety_points
                    ).toPrecision(5)}
                  </TableCell>
                </TableRow>
              )
            })}
        </TableBody>
      </Table>
    </div>
  )
}
